// /catalog dispatch. A client browses/orders their own catalogue. A rep or internal staff member
// picks a client HERE (in-module, ephemeral — no global/persisted selection); the flow then opens
// scoped to that client.
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore.js';
import OrderFlow from './OrderFlow.jsx';
import ClientPicker from '../components/ClientPicker.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fetchMyLocations, fetchRepClients } from '../api/me.js';
import { storeShort } from '../lib/stores.js';

export default function CatalogRoute() {
  const role = useAuthStore((s) => s.user?.role);
  const location = useLocation();
  // Local, non-persisted. Seeded only if we arrived from a "Start order" action.
  const [client, setClient] = useState(location.state?.client || null);

  if (role === 'CLIENT') return <ClientCatalog />;

  // A client contact is optional (a location with no provisioned login can still be ordered for).
  const scope = client ? { store: client.store, companyLocationId: client.shopifyCompanyLocationId } : null;
  if (scope && client.shopifyCompanyContactId) scope.companyContactId = client.shopifyCompanyContactId;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-text-secondary">Ordering for:</span>
        <ClientPicker value={client} onSelect={setClient} />
        {client && (
          <button onClick={() => setClient(null)} className="text-xs text-text-muted hover:text-text-primary hover:underline">
            change
          </button>
        )}
      </div>

      {!client ? (
        <>
          <PageHeader title="New order" subtitle="Who is this order for? Their own range and prices load once you pick." />
          <ClientChooser onSelect={setClient} />
        </>
      ) : (
        <OrderFlow
          key={client.onBehalfOfClientUserId || client.shopifyCompanyLocationId}
          scope={scope}
          onBehalfOf={client.onBehalfOfClientUserId || undefined}
          onBehalfLabel={client.locationName ? `${client.companyName} · ${client.locationName}` : client.companyName}
        />
      )}
    </>
  );
}

/**
 * A client's own catalogue. Most clients have exactly one store and see no picker at all — it only
 * appears for a head-office buyer whose login may order for several of the company's stores. The
 * chosen store decides where the order books and ships, so it's picked BEFORE the order, not at the
 * end: switching it re-keys OrderFlow, which clears a part-built cart rather than silently moving it.
 */
function ClientCatalog() {
  const [locations, setLocations] = useState(null);
  const [chosen, setChosen] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchMyLocations()
      .then((list) => {
        if (!alive) return;
        setLocations(list);
        setChosen(list?.[0]?.companyLocationId || null); // mapped store first = the default
      })
      // A failure here must not block ordering: fall back to server-side scope resolution.
      .catch(() => alive && setLocations([]));
    return () => {
      alive = false;
    };
  }, []);

  if (locations === null) {
    return <div className="rounded-lg border border-line bg-surface p-10 text-center text-sm text-text-muted">Loading…</div>;
  }

  // One store (or the lookup failed) → exactly the old behaviour: no picker, scope resolved server-side.
  if (locations.length <= 1) return <OrderFlow key="self" />;

  const active = locations.find((l) => l.companyLocationId === chosen) || locations[0];
  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-text-secondary" htmlFor="client-store">
          Ordering for:
        </label>
        <select
          id="client-store"
          value={active.companyLocationId}
          onChange={(e) => setChosen(e.target.value)}
          className="rounded-md border border-line bg-bg px-2 py-1.5 text-sm text-text-primary"
        >
          {locations.map((l) => (
            <option key={l.companyLocationId} value={l.companyLocationId}>
              {l.locationName || l.companyName}
            </option>
          ))}
        </select>
        <span className="text-xs text-text-muted">This order ships to, and is invoiced against, the store you pick.</span>
      </div>
      <OrderFlow key={active.companyLocationId} scope={{ store: active.store, companyLocationId: active.companyLocationId }} />
    </>
  );
}

// The rep's / staff member's clients as a searchable list — one click starts the order (the small
// dropdown above stays for switching mid-order).
function ClientChooser({ onSelect }) {
  const [clients, setClients] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => {
    fetchRepClients().then(setClients).catch(() => setClients([]));
  }, []);
  if (!clients) return <div className="text-sm text-text-muted">Loading your clients…</div>;
  const needle = q.trim().toLowerCase();
  const shown = clients.filter((c) => !needle || `${c.companyName || ''} ${c.locationName || ''} ${c.clientEmail || ''}`.toLowerCase().includes(needle));
  if (!clients.length) {
    return <div className="rounded-lg border border-dashed border-line bg-surface p-10 text-center text-sm text-text-muted">No clients assigned to you yet.</div>;
  }
  return (
    <div className="rounded-lg border border-line bg-surface p-3 shadow-sm">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by client, store or email…"
        aria-label="Search clients"
        className="mb-2 w-full rounded-md border border-line px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
      />
      <div className="grid max-h-[60vh] gap-1.5 overflow-auto sm:grid-cols-2">
        {shown.map((c) => (
          <button
            key={c.onBehalfOfClientUserId || c.shopifyCompanyLocationId}
            onClick={() => onSelect(c)}
            className="rounded-md border border-line px-3 py-2 text-left transition hover:border-accent hover:bg-accent-light/40"
          >
            <div className="text-sm font-medium text-text-primary">{c.companyName}</div>
            <div className="text-xs text-text-muted">
              {c.locationName || 'Store'} · {storeShort(c.store)}
              {c.clientEmail ? ` · ${c.clientEmail}` : ''}
            </div>
          </button>
        ))}
        {!shown.length && <div className="px-2 py-4 text-sm text-text-muted">No clients match “{q}”.</div>}
      </div>
    </div>
  );
}
